import React, { ComponentProps, useMemo } from 'react';
import {
  Nominal,
  NominalAuditByField as auditEntity,
  NominalBudgetProfile as nominalBudgetProfile,
} from '../../graphql/entities';
import createForm from '../../components/connectedForm/ConnectedForm';
import { default as Form } from '../../components/connectedForm';
import * as input from '../../components/connectedForm/inputComponents';
import GridContainer from '../../components/connectedForm/GridContainer';
import Formatters from '../../components/grid-addons/formatters';
import getIn from '../../components/connectedForm/utils/getIn';
import NominalTransactionWorkspace from '../../views/nominal/NominalTransactionWorkspace';
import ValueCard from '../../components/connectedForm/ValueCard';
import NominalAnalysis from './NominalAnalysis';
//Audit Tab
import AuditByFieldWorkspace from '../../views/company/AuditByFieldWorkspace';
import EditableDataGrid from '../../components/EditableDataGrid/EditableDataGrid';
import BudgetTab from './BudgetTab';
import {
  FilterOperation,
  IFilterInput,
  INominal,
  INominalBudget,
  INominalNotesQueryResult,
  INominalNotesQueryVariables,
  INominalPeriodBalance,
} from '../../graphql/schema/p4types';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import { IBlurTextFieldProps } from '../../components/connectedForm/BlurTextField/BlurTextField';
import { INominalPeriodTransactionsProps, INominalTransactionWorkspaceProps } from './NominalPeriodTransactions';
import { useFormQuery } from '../../graphql/containers/useFormQuery';
import { EntityType } from '../../@types/entityTypes';
import { useMutation } from '@apollo/client';
import { useCompany } from '../../utils/FetchConfig';
import { IDateFieldProps } from '../../components/connectedForm/DateInput/DateInput';
import MemoEditor, { IMemoEditorType, useMemoEditorProps } from '../../components/connectedForm/MemoEditor/MemoEditor';
import NominalNotesWorkspaceQuery from '../../graphql/queries/nominal/NominalNotesWorkspaceQuery.graphql';
import NominalNoteType from '../../graphql/entities/nominal/NominalNoteType';
import useEntityCacheUpdate from '../../graphql/hooks/useEntityCacheUpdate';
import { NumericModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import { NumberRange } from '../../@types/numberRange';

const LazyNominalPeriodTransactions = React.lazy(() => import('./NominalPeriodTransactions'));

const getBalanceBroughtForward = (values: INominal) => {
  const { balance } = values;
  return balance ? balance.baseBalanceBroughtForward : 0;
};

type IExtendedNominalPeriodBalance = INominalPeriodBalance & { budget?: number };

type INominalFormProps = {
  values: INominal;
  match: {
    params: { id: number };
  };
  activeTabId?: string;
  history: History;
  pageView?: boolean;
};

const NominalForm = (props: INominalFormProps) => {
  const company = useCompany();

  const memoEditorProps = useMemoEditorProps<INominalNotesQueryResult, INominalNotesQueryVariables>(
    NominalNotesWorkspaceQuery,
    IMemoEditorType.Nominal,
    {
      predefinedFilters: [
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'nominal.id',
            value: props.values?.id,
          },
        },
      ],
    }
  );

  // match.params.id is only set when viewing from a normal form, not when viewed in workspace form
  // view so when using workspace form view we need to use the props.values.id setting
  const accountId = props.match?.params?.id || props.values.id;
  const currentYear = company?.currentFinancialYear;

  let transactionPredefinedFilters: Array<IFilterInput>;
  let nominalAuditPredefinedFilters: Array<IFilterInput>;
  let newAccount;

  if (accountId && accountId !== 'New') {
    transactionPredefinedFilters = [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'nominal.id',
          value: accountId,
        },
      },
    ];

    nominalAuditPredefinedFilters = [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'id',
          value: accountId,
        },
      },
    ];
    newAccount = false;
  } else {
    transactionPredefinedFilters = [];
    nominalAuditPredefinedFilters = [];
    newAccount = true;
  }

  const getTransactionModalProps: (
    value: number,
    values?: any
  ) => Omit<INominalPeriodTransactionsProps, 'onClose'> & INominalTransactionWorkspaceProps = (
    value,
    modalProps: { rowData: IExtendedNominalPeriodBalance }
  ) => {
    return {
      embeddedView: true,
      ytdBalance: modalProps.rowData.baseYtdBalance,
      periodBalance: modalProps.rowData.baseBalance,

      additionalVariables: { excludeNominal: true },
      viewScreenName: '/Nominal/NominalTransaction/Modal',
      predefinedFilters: [
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'nominal.id',
            value: accountId,
          },
        },
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'financialPeriod.id',
            value: `${modalProps.rowData.period?.id}`,
          },
        },
      ],
    };
  };

  const periodColumns: Array<IEditableDataGridColumnDef> = [
    {
      name: 'Period',
      key: 'period.periodNumber',
      resizable: false,
      width: 65,
    },
    {
      name: 'Start Date',
      key: 'period.startDate',
      formatter: Formatters.DateFormatter,
      resizable: true,
    },
    {
      name: 'Budget',
      key: 'budget',
      cellClass: 'rightAlign',
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals: 2 },
      },
    },
    {
      name: 'Period Balance',
      key: 'baseBalance',
      cellClass: 'rightAlign',
      formatter: NumericModalFormatter,
      options: {
        format: { decimals: 2 },
        modalProps: getTransactionModalProps,
        modalContent: LazyNominalPeriodTransactions,
      },
    },
    {
      name: 'YTD Balance',
      key: 'baseYtdBalance',
      cellClass: 'rightAlign',
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals: 2 },
      },
    },
  ];

  /**
   * Merge the account balances and budgets for display in grid
   */
  const balances = useMemo<Array<IExtendedNominalPeriodBalance>>(() => {
    const { balance, nominalBudgets } = props.values;
    const periodBalances: Array<IExtendedNominalPeriodBalance> =
      (balance?.currentYearPeriodList as Array<INominalPeriodBalance>) || [];
    const budgets = Array.isArray(nominalBudgets)
      ? (nominalBudgets as Array<INominalBudget>).find((budget) => budget.financialCalendar?.id === currentYear?.id)
      : null;

    if (!budgets) return periodBalances;

    const balances: Array<IExtendedNominalPeriodBalance> = [];
    if (Array.isArray(periodBalances)) {
      for (const period of periodBalances) {
        const row = Object.assign({}, period) as IExtendedNominalPeriodBalance;
        row.budget = budgets[`period${period!.period!.periodNumber as NumberRange<1, 12>}`];
        balances.push(row);
      }
    }
    return balances;
  }, [currentYear?.id, props.values]);

  const baseBalanceBroughtForward = getBalanceBroughtForward(props.values);

  return (
    <Form.FormContainer>
      <Form.Header entityHdr="accountNo" entityHdr2="name" component={Form.HeaderBlock} />
      <Form.Form>
        <Form.TabPanel>
          <Form.TabContent id="main" label="General">
            <Form.Row>
              <Form.Fieldset cols={1 / 2}>
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Legend label="Account Information" />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="accountNo"
                      component={input.BlurTextField}
                      label="Account No."
                      autoFocus={true}
                    />
                    <Form.Field dataid="name" component={input.BlurTextField} label="Name" />
                    <Form.Field dataid="accountType" component={input.Dropdown} label="Account Type" />
                    <Form.Field dataid="reportCategory" component={input.Dropdown} label="Report Category" />
                  </Form.Fieldset>
                </Form.Row>
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Legend label="Postings" />
                    <Form.Field dataid="allowDirectPostings" component={input.Checkbox} label="Allow Direct?" />
                    <Form.Field dataid="postingSuspended" component={input.Checkbox} label="Suspended?" />
                    <Form.Field dataid="bankAccount" component={input.Checkbox} label="Bank Account?" readOnly={true} />
                  </Form.Fieldset>
                </Form.Row>
                <Form.Row>
                  <Form.Fieldset cols={1}>
                    <Form.Legend label="Other Information" />
                    <Form.Field dataid="createdBy" component={input.BlurTextField} label="Created By" readOnly={true} />
                    <Form.Field<IDateFieldProps>
                      dataid="createdAt"
                      component={input.DateField}
                      label="Date/Time Created"
                      dateFormat="iii do MMM yyyy HH:mm"
                      readOnly={true}
                    />
                    <Form.Field<IBlurTextFieldProps>
                      dataid="updatedBy"
                      component={input.BlurTextField}
                      label="Last Updated By"
                      readOnly={true}
                    />
                    <Form.Field<IDateFieldProps>
                      dataid="updatedAt"
                      component={input.DateField}
                      label="Date/Time Updated"
                      dateFormat="iii do MMM yyyy HH:mm"
                      readOnly={true}
                    />
                  </Form.Fieldset>
                </Form.Row>
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 2}>
                {!newAccount ? (
                  <>
                    <Form.Row>
                      <Form.Fieldset cols={1}>
                        <Form.Row>
                          <Form.Fieldset cols={1 / 2}>
                            <ValueCard
                              label="Balance B/Forward"
                              decimals={2}
                              value={baseBalanceBroughtForward}
                              labelCols={1 / 2}
                              inputCols={1 / 2}
                              type={'baseCurrency'}
                            />
                          </Form.Fieldset>
                          <Form.Fieldset cols={1 / 2}>
                            <Form.Field<any>
                              dataid="balance.baseBalance"
                              component={input.ValueCard}
                              label="YTD Balance"
                              decimals={2}
                              labelCols={1 / 2}
                              inputCols={1 / 2}
                              type={'baseCurrency'}
                            />
                          </Form.Fieldset>
                        </Form.Row>
                      </Form.Fieldset>
                    </Form.Row>
                    <Form.Row>
                      <Form.Fieldset cols={1}>
                        <EditableDataGrid
                          listData={balances}
                          rowsCount={balances.length}
                          className="displayGrid"
                          columns={periodColumns}
                          enableCellSelect={false}
                          enableCellAutoFocus={false}
                          addBlankRow={false}
                          initialSortColumn="period.periodNumber"
                          readOnly={true}
                          autoContentHeight={true}
                          emptyRowsView={() => <div />}
                        />
                      </Form.Fieldset>
                    </Form.Row>
                  </>
                ) : null}
              </Form.Fieldset>
            </Form.Row>
          </Form.TabContent>
          <Form.TabContent
            id="notesTab"
            label="Notes"
            badge={memoEditorProps.queryProps.data?.listData?.count}
            type="info"
            onlyRenderWhenActive={true}
            shouldRender={!newAccount}
          >
            <MemoEditor noteTypeEntity={NominalNoteType} {...memoEditorProps} />
          </Form.TabContent>
          <Form.TabContent id="budgetsTab" label="Budgets" shouldRender={!!currentYear}>
            <BudgetTab type="nominal" profileDropdown={nominalBudgetProfile.dropdown} />
          </Form.TabContent>
          <Form.TabContent id="analysis" label="Analytics" shouldRender={currentYear && accountId !== 'New'}>
            <Form.Row>
              <Form.Fieldset cols={1}>
                <NominalAnalysis nominal={accountId} />
              </Form.Fieldset>
            </Form.Row>
          </Form.TabContent>
          <Form.TabContent id="transactions" label="Transactions" shouldRender={!newAccount}>
            <GridContainer>
              <NominalTransactionWorkspace
                embeddedView={true}
                pageView={props.pageView}
                additionalVariables={{ excludeNominal: true }}
                predefinedFilters={transactionPredefinedFilters}
                viewScreenName="/Nominal/NominalTransaction/Modal"
              />
            </GridContainer>
          </Form.TabContent>
          <Form.TabContent id="audit" label="Audit" shouldRender={!newAccount}>
            <GridContainer>
              <AuditByFieldWorkspace
                embeddedView={true}
                pageView={props.pageView}
                predefinedFilters={nominalAuditPredefinedFilters}
                viewScreenName="/nominal/NominalAudit/Workspace"
                entity={auditEntity}
              />
            </GridContainer>
          </Form.TabContent>
        </Form.TabPanel>
      </Form.Form>
    </Form.FormContainer>
  );
};

const {
  default: defaultQuery,
  formTitle,
  workspaceUrl,
  temporary,
  update,
  instance,
  fragment,
  temporaryUpdate,
  type,
} = Nominal;

const ConnectedNominalForm: any = createForm({
  form: type,
  name: formTitle,
  entity: Nominal,
  queryFragment: fragment,
  defaultReturnUrl: workspaceUrl,
  shouldComponentUpdate: (props: ComponentProps<typeof NominalForm>, nextProps: ComponentProps<typeof NominalForm>) => {
    return props !== nextProps || getIn(props, 'values.contacts') !== getIn(nextProps, 'values.contacts');
  },
})(NominalForm);

export default ({
  temporaryDefault,
  overrideParams,
  detailView,
  ...props
}: ComponentProps<typeof ConnectedNominalForm> & {
  temporaryDefault: boolean;
  overrideParams: any;
  detailView?: boolean;
}) => {
  const formDefault = useFormQuery(
    (temporaryDefault
      ? { default: temporary, instance: instance }
      : { default: defaultQuery, instance: instance }) as EntityType,
    temporaryDefault ? { forceDefault: true, idParam: 'uuid', overrideParams } : { duplicate: true, overrideParams }
  );
  const cacheUpdate = useEntityCacheUpdate('nominals', 'nominal', { detailView });
  const [formUpdate] = useMutation(update!, { update: cacheUpdate, refetchQueries: [NominalNotesWorkspaceQuery] });
  const [formTemporaryUpdate] = useMutation(temporaryUpdate!);

  return (
    <ConnectedNominalForm
      {...props}
      formUpdate={formUpdate}
      formTemporaryUpdate={formTemporaryUpdate}
      formData={formDefault}
    />
  );
};
